import { Fragment, HTMLAttributes, ReactNode, useId } from 'react';
import { twMerge } from 'tailwind-merge';
import { Badge, Typography } from '../../ui';
import { Link } from 'react-aria-components';
import { IconClock } from '@maia/icons';
import { widgetStyles, WidgetStylesVariants } from '../../patterns/widget';

type Props = {
  title: string | { title: string; href: string };
  subtitle?: string;
  icon?: ReactNode;
  timestamp?: { showIcon?: boolean; date: string };
  actions?: Array<ReactNode>;
  padding?: WidgetStylesVariants['padding'];
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>;

export function Widget(props: Props) {
  const id = useId();
  const {
    title,
    subtitle,
    icon: Icon,
    timestamp,
    actions,
    className,
    children,
    padding = 'normal',
    ...rest
  } = props;

  return (
    <div
      className={twMerge(
        widgetStyles({ variant: 'elevated', padding }),
        'flex flex-row items-center space-x-40',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-row items-center space-x-12 flex-1">
        {Icon}

        <div className="flex flex-col">
          {typeof title === 'string' ? (
            <Typography
              variant="body-xs"
              weight="medium"
              className="text-grayscale600"
            >
              {title}
            </Typography>
          ) : (
            <Link href={title.href}>
              <Typography
                variant="body-xs"
                weight="medium"
                className="text-info300"
              >
                {title.title}
              </Typography>
            </Link>
          )}

          {subtitle && (
            <Typography
              variant="body-xs"
              weight="regular"
              className="text-grayscale300 line-clamp-1"
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </div>

      {(children || timestamp) && (
        <div className="flex flex-row items-center space-x-12">
          {children}

          {timestamp && (
            <Badge
              icon={timestamp.showIcon ? IconClock : undefined}
              label={timestamp.date}
              className="justify-self-end ml-auto"
              variant="disabled"
              spacing="small"
            />
          )}
        </div>
      )}

      {actions && (
        <div className="flex items-center space-x-12">
          {actions.map((action, key) => (
            <Fragment key={`widget-${id}-action-${key}`}>{action}</Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
